
import type { World } from "./collision";
import { HEADING_MASK, cosFx, mulFx, sinFx } from "./fixed";

export const TICK_HZ = 20;
export const TICK_MS = 1000 / TICK_HZ;
/** 50 ms. The original ran at 25 Hz; 20 divides 1000 evenly, which keeps
 *  tick-to-millisecond conversion exact and avoids the accumulating remainder a
 *  25 Hz clock produces when reconciling against wall time. */

/** What the player is asking to do, not how fast they claim to go. */
export const Gait = {
  IDLE: 0,
  WALK: 1,
  RUN: 2,
} as const;

export type Gait = (typeof Gait)[keyof typeof Gait];

const GAIT_VALUES: readonly number[] = [Gait.IDLE, Gait.WALK, Gait.RUN];

export interface MoveStats {
  readonly walkMmS: number;
  readonly runMmS: number;
  /** 0.6 in 16.16 -- wading is slow. */
  readonly waterScaleFx: number;
}

export const DEFAULT_STATS: MoveStats = {
  walkMmS: 2_211,
  runMmS: 4_500,
  waterScaleFx: 39_321,
};

export function speedFor(stats: MoveStats, gait: Gait): number {
  if (gait === Gait.RUN) return stats.runMmS;
  if (gait === Gait.WALK) return stats.walkMmS;
  return 0;
}

export function groundSpeedMmS(state: MoveState, stats: MoveStats, world: World): number {
  const speed = speedFor(stats, state.gait);
  if (speed === 0) return 0;
  return world.isWater(state.xMm, state.yMm) ? mulFx(speed, stats.waterScaleFx) : speed;
}

/** Everything the movement step reads and writes. */
export interface MoveState {
  readonly xMm: number;
  readonly yMm: number;
  readonly heading: number;
  readonly gait: Gait;
}

export function moveState(
  xMm: number,
  yMm: number,
  heading = 0,
  gait: Gait = Gait.IDLE,
): MoveState {
  return { xMm, yMm, heading, gait };
}

export interface MoveInput {
  readonly seq: number;
  readonly heading: number;
  readonly gait: Gait;
}

export function sanitised(move: MoveInput): MoveInput {
  const gait = GAIT_VALUES.includes(move.gait) ? move.gait : Gait.IDLE;
  return { seq: move.seq, heading: move.heading & HEADING_MASK, gait };
}

function slide(
  state: MoveState,
  dx: number,
  dy: number,
  world: World,
): { x: number; y: number } {
  const x = state.xMm;
  const y = state.yMm;
  const targetX = x + dx;
  const targetY = y + dy;

  // The attribute grid, then the static collision objects a moving actor is
  // tested against (`PythonBackground.cpp:569-596`).
  const blocked = (tx: number, ty: number) =>
    world.isBlocked(tx, ty) || (world.blocksStep?.(x, y, tx, ty) ?? false);

  if (!blocked(targetX, targetY)) return { x: targetX, y: targetY };

  // Blocked diagonally: keep whichever single-axis move is legal.
  if (dx !== 0 && !blocked(targetX, y)) return { x: targetX, y };
  if (dy !== 0 && !blocked(x, targetY)) return { x, y: targetY };
  return { x, y };
}

/** Three of the original's frames to a tick: its fixed-step clock advances
 *  16 and 17 ms in turn (`Timer.cpp:94`), and a tick is 50 ms. */
export const FRAMES_PER_TICK = 3; // Timer.cpp:94

/** One tick's move of (dx, dy). A step no object stops is `slide`'s. One an
 *  object stops is walked as the original's frames, each frame's step slid
 *  along whatever it touches (`PythonBackground.cpp:569-596`) and the rest of
 *  the tick given up where a frame is blocked outright. */
function advance(state: MoveState, dx: number, dy: number, world: World): { x: number; y: number } {
  const x = state.xMm;
  const y = state.yMm;
  if (world.avoidStep === undefined || !(world.blocksStep?.(x, y, x + dx, y + dy) ?? false)) {
    return slide(state, dx, dy, world);
  }
  let at = { x, y };
  for (let frame = 0; frame < FRAMES_PER_TICK; frame++) {
    const fx = Math.trunc((dx * (frame + 1)) / FRAMES_PER_TICK) - Math.trunc((dx * frame) / FRAMES_PER_TICK);
    const fy = Math.trunc((dy * (frame + 1)) / FRAMES_PER_TICK) - Math.trunc((dy * frame) / FRAMES_PER_TICK);
    const here: MoveState = { xMm: at.x, yMm: at.y, heading: state.heading, gait: state.gait };
    if (!world.blocksStep!(at.x, at.y, at.x + fx, at.y + fy)) {
      at = slide(here, fx, fy, world);
      continue;
    }
    const slid = world.avoidStep(at.x, at.y, fx, fy);
    if (slid === null || world.isBlocked(slid[0], slid[1])) break;
    at = { x: slid[0], y: slid[1] };
  }
  return at;
}

export function step(
  state: MoveState,
  input: MoveInput,
  stats: MoveStats,
  world: World,
  ticks = 1,
  locked = false,
): MoveState {
  if (ticks <= 0) return state;

  const move = sanitised(input);
  if (locked) {
    return { xMm: state.xMm, yMm: state.yMm, heading: move.heading, gait: Gait.IDLE };
  }
  const speed = speedFor(stats, move.gait);

  let current: MoveState = {
    xMm: state.xMm,
    yMm: state.yMm,
    heading: move.heading,
    gait: move.gait,
  };
  if (speed === 0) return current;

  for (let i = 0; i < ticks; i++) {
    const effective = groundSpeedMmS(current, stats, world);

    // Distance this tick, in mm. Integer division truncates, which is fine and
    // identical in both languages -- `effective` is never negative, so Python's
    // flooring `//` and this floor agree. The lost sub-millimetre is below
    // anything renderable.
    const distance = Math.floor((effective * TICK_MS) / 1000);

    const dx = mulFx(distance, cosFx(current.heading));
    const dy = mulFx(distance, sinFx(current.heading));

    const moved = advance(current, dx, dy, world);
    current = {
      xMm: moved.x,
      yMm: moved.y,
      heading: current.heading,
      gait: current.gait,
    };
  }

  return current;
}
